import React, { useState, useEffect } from 'react';
import { useEvent } from '../context/EventContext';
import { useUserProfile } from '../hooks/useUserProfile';
import Button from '../components/Button';
import GuestGroupsView from '../components/GuestGroupsView';

const Guests = () => {
  const { eventData, guestCount, loading: eventLoading } = useEvent();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    dietaryRequirements: '', 
    tableNumber: '',
    group: '',
    notes: '',
    rsvpStatus: 'pending' 
  });
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'groups'
  
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (showModal || showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showModal, showEditModal]);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);

  const loadGuests = async () => {
    if (!eventData?.eventId) return;
    
    try {
      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventData.eventId}`);
      if (response.ok) {
        const data = await response.json();
        const standardizedGuests = data.map((guest, index) => {
          const standardized = {};
          for (const [key, value] of Object.entries(guest)) {
            if (value?.S !== undefined) standardized[key] = value.S;
            else if (value?.N !== undefined) standardized[key] = parseInt(value.N);
            else if (value?.BOOL !== undefined) standardized[key] = value.BOOL;
            else standardized[key] = value;
          }
          return {
            id: standardized.id || standardized.SK?.replace('GUEST#', '') || `guest-${index}-${Date.now()}`,
            name: standardized.name || '',
            email: standardized.email || '',
            phone: standardized.phone || '',
            dietaryRequirements: standardized.dietaryRequirements || '',
            tableNumber: standardized.tableNumber || '',
            notes: standardized.notes || '',
            group: standardized.group || '',
            rsvpStatus: standardized.rsvpStatus || 'pending'
          };
        });
        setGuests(standardizedGuests);
      }
    } catch (error) {
      console.error('Error loading guests:', error);
      setGuests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuests();
  }, [eventData]);

  const handleAddGuest = async (e) => {
    e.preventDefault();
    if (!eventData?.eventId) return;
    
    setSubmitting(true);
    try {
      const { Auth } = await import('aws-amplify');
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      
      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventData.eventId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          dietaryRequirements: formData.dietaryRequirements,
          tableNumber: formData.tableNumber,
          notes: formData.notes,
          rsvpStatus: formData.rsvpStatus,
          createdByUserId: user.username
        })
      });
      
      if (response.ok) {
        setShowModal(false);
        setFormData({ 
          name: '', 
          email: '', 
          phone: '',
          dietaryRequirements: '', 
          tableNumber: '',
          notes: '',
          rsvpStatus: 'pending' 
        });
        await loadGuests();
      }
    } catch (error) {
      console.error('Error adding guest:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditGuest = async (e) => {
    e.preventDefault();
    if (!eventData?.eventId || !editingGuest) return;
    
    setSubmitting(true);
    try {
      const { Auth } = await import('aws-amplify');
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      
      const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventData.eventId}/${editingGuest.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          rsvpStatus: formData.rsvpStatus,
          dietaryRequirements: formData.dietaryRequirements,
          tableNumber: formData.tableNumber,
          group: formData.group,
          notes: formData.notes,
        })
      });
      
      if (response.ok) {
        setShowEditModal(false);
        setEditingGuest(null);
        setFormData({ 
          name: '', 
          email: '', 
          phone: '',
          dietaryRequirements: '', 
          tableNumber: '',
          notes: '',
          rsvpStatus: 'pending' 
        });
        await loadGuests();
      } else {
        const errorData = await response.text();
        console.error('Update failed:', response.status, errorData);
        alert('Failed to update guest. Please try again.');
      }
    } catch (error) {
      console.error('Error updating guest:', error);
      alert('Error updating guest: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (eventLoading || profileLoading || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading guests...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '32px 24px 140px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '16px'
      }}>
        <h1 className="page-title" style={{ 
          fontSize: '42px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          Guests
        </h1>
        
        <div className="stats-grid" style={{ 
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          gap: '16px'
        }}>
          <div className="stats-card" style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {guests.filter(g => g.rsvpStatus === 'confirmed').length} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>confirmed</span>
              </div>
            </div>
          </div>
          <div className="stats-card" style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {guests.filter(g => g.rsvpStatus === 'pending').length} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>pending</span>
              </div>
            </div>
          </div>
          <div className="stats-card" style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {guestCount} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>invited</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {/* View Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button onClick={() => setViewMode('list')}
            style={{ padding: '8px 16px', backgroundColor: viewMode === 'list' ? '#6B9080' : 'white', color: viewMode === 'list' ? 'white' : '#6B7280', border: viewMode === 'list' ? 'none' : '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
            List View
          </button>
          <button onClick={() => setViewMode('groups')}
            style={{ padding: '8px 16px', backgroundColor: viewMode === 'groups' ? '#6B9080' : 'white', color: viewMode === 'groups' ? 'white' : '#6B7280', border: viewMode === 'groups' ? 'none' : '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
            Groups View
          </button>
        </div>

        {viewMode === 'groups' ? (
          <GuestGroupsView guests={guests} eventId={eventData?.eventId} onUpdate={(guestId, groupId) => {
            setGuests(prev => prev.map(g => g.id === guestId ? { ...g, group: groupId === 'unassigned' ? '' : groupId } : g));
          }} />
        ) : guests.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
            <h3 style={{ fontSize: '24px', fontFamily: 'Georgia, serif', marginBottom: '12px' }}>No Guests Yet</h3>
            <p>Start building your guest list for the wedding.</p>
          </div>
        ) : (
          <div>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 100px', gap: '12px', padding: '16px 24px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', fontWeight: '600', fontSize: '14px', color: '#6B7280' }}>
                <div>Name</div>
                <div>Email</div>
                <div>Phone</div>
                <div>Dietary</div>
                <div>Group</div>
                <div>RSVP</div>
                <div></div>
              </div>
            
              {guests.map((guest, index) => (
                <div key={guest.id || `guest-${index}`} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1.2fr 1.2fr 0.8fr 0.8fr 0.8fr 0.8fr 100px', 
                  gap: '12px', 
                  padding: '16px 24px', 
                  borderBottom: index < guests.length - 1 ? '1px solid #E5E7EB' : 'none',
                  fontSize: '14px',
                  alignItems: 'center'
                }}>
                  <div style={{ fontWeight: '500', color: '#1F2937' }}>{guest.name}</div>
                  <div style={{ color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{guest.email}</div>
                  <div style={{ color: '#6B7280', fontSize: '13px' }}>{guest.phone || '-'}</div>
                  <div style={{ color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>{guest.dietaryRequirements || 'None'}</div>
                  <div style={{ color: '#6B7280' }}>{guest.group ? guest.group.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'}</div>
                  <div>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: guest.rsvpStatus === 'confirmed' ? '#D1FAE5' : guest.rsvpStatus === 'declined' ? '#FEE2E2' : '#FEF3C7',
                      color: guest.rsvpStatus === 'confirmed' ? '#065F46' : guest.rsvpStatus === 'declined' ? '#991B1B' : '#92400E'
                    }}>
                      {guest.rsvpStatus === 'confirmed' ? 'Confirmed' : guest.rsvpStatus === 'declined' ? 'Declined' : 'Pending'}
                    </span>
                  </div>
                  <div>
                    <button
                      onClick={() => {
                        setEditingGuest(guest);
                        setFormData({
                          name: guest.name || '',
                          email: guest.email || '',
                          phone: guest.phone || '',
                          rsvpStatus: guest.rsvpStatus || 'pending',
                          dietaryRequirements: guest.dietaryRequirements || '',
                          tableNumber: guest.tableNumber || '',
                          group: guest.group || '',
                          notes: guest.notes || '',
                        });
                                        setShowEditModal(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#E8F4E8',
                        color: '#6B9080',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '32px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={() => setShowModal(true)}>Add Guest</Button>
              <button
                onClick={() => {
                  // Download CSV template
                  const template = 'Name,Email,Phone,Dietary Requirements,Group,RSVP Status,Notes\n"John Doe","john@example.com","+64 21 123 4567","Vegetarian","bridal-party","pending","Allergic to peanuts"\n"Jane Smith","jane@example.com","+64 21 987 6543","Gluten-free","table-1","confirmed",""';
                  const blob = new Blob([template], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'guest-list-template.csv';
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#6B9080',
                  border: '2px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Download Template
              </button>
              <button
                onClick={() => document.getElementById('csv-upload').click()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#6B9080',
                  border: '2px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Import CSV
              </button>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const text = await file.text();
                  const lines = text.split('\n').filter(l => l.trim());
                  const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
                  const nameIdx = headers.findIndex(h => h.includes('name'));
                  const emailIdx = headers.findIndex(h => h.includes('email'));
                  const phoneIdx = headers.findIndex(h => h.includes('phone'));
                  const dietaryIdx = headers.findIndex(h => h.includes('dietary'));
                  const tableIdx = headers.findIndex(h => h.includes('table'));
                  const groupsIdx = headers.findIndex(h => h.includes('group'));
                  const rsvpIdx = headers.findIndex(h => h.includes('rsvp'));
                  const notesIdx = headers.findIndex(h => h.includes('notes'));
                  
                  let imported = 0;
                  let skipped = 0;
                  
                  for (let i = 1; i < lines.length; i++) {
                    const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''));
                    if (!cols[nameIdx]) continue;
                    
                    const name = cols[nameIdx];
                    const email = cols[emailIdx] || '';
                    
                    // Check for duplicates
                    const isDuplicate = guests.some(g => 
                      g.name.toLowerCase() === name.toLowerCase() || 
                      (email && g.email && g.email.toLowerCase() === email.toLowerCase())
                    );
                    
                    if (isDuplicate) {
                      skipped++;
                      continue;
                    }
                    
                    try {
                      const { Auth } = await import('aws-amplify');
                      const user = await Auth.currentAuthenticatedUser();
                      const token = user.signInUserSession.idToken.jwtToken;
                      
                      await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventData.eventId}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({
                          name: name,
                          email: email,
                          phone: phoneIdx >= 0 ? cols[phoneIdx] : '',
                          dietaryRequirements: dietaryIdx >= 0 ? cols[dietaryIdx] : '',
                          tableNumber: tableIdx >= 0 ? cols[tableIdx] : '',
                          group: groupsIdx >= 0 ? cols[groupsIdx] : '',
                          notes: notesIdx >= 0 ? cols[notesIdx] : '',
                          rsvpStatus: rsvpIdx >= 0 ? cols[rsvpIdx]?.toLowerCase() : 'pending',
                          createdByUserId: user.username
                        })
                      });
                      imported++;
                    } catch (error) {
                      console.error('Error importing guest:', error);
                    }
                  }
                  await loadGuests();
                  alert(`Import complete! ${imported} guests added, ${skipped} duplicates skipped.`);
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => {
                  const csv = ['Name,Email,Phone,Dietary Requirements,Group,RSVP Status,Notes'];
                  guests.forEach(g => {
                    csv.push(`"${g.name}","${g.email}","${g.phone || ''}","${g.dietaryRequirements || ''}","${g.group || ''}","${g.rsvpStatus}","${g.notes || ''}"`);
                  });
                  const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${eventData?.name || 'event'}-guests.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#6B9080',
                  border: '2px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Export to CSV
              </button>
            </div>
          </div>
        )}
        
        {guests.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Button variant="primary" onClick={() => setShowModal(true)}>Add Guest</Button>
          </div>
        )}
      </div>
      
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            margin: '20px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '24px',
              fontFamily: 'Georgia, serif'
            }}>Add New Guest</h2>
            
            <form onSubmit={handleAddGuest}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="e.g. +64 21 123 4567"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Table Number</label>
                <input
                  type="text"
                  value={formData.tableNumber}
                  onChange={(e) => setFormData({...formData, tableNumber: e.target.value})}
                  placeholder="e.g. 1, 2, 3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Dietary Requirements</label>
                <input
                  type="text"
                  value={formData.dietaryRequirements}
                  onChange={(e) => setFormData({...formData, dietaryRequirements: e.target.value})}
                  placeholder="e.g. Vegetarian, Gluten-free, No nuts"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any additional notes about this guest"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>RSVP Status</label>
                <select
                  value={formData.rsvpStatus}
                  onChange={(e) => setFormData({...formData, rsvpStatus: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setFormData({ name: '', email: '', dietaryRequirements: '', rsvpStatus: 'pending' });
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.name}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: submitting || !formData.name ? '#9CA3AF' : '#6B9080',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: submitting || !formData.name ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Adding...' : 'Add Guest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showEditModal && editingGuest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            margin: '20px'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '24px',
              fontFamily: 'Georgia, serif'
            }}>Edit Guest: {editingGuest.name}</h2>
            
            <form onSubmit={handleEditGuest}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="e.g. +64 21 123 4567"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Table Number</label>
                <input
                  type="text"
                  value={formData.tableNumber}
                  onChange={(e) => setFormData({...formData, tableNumber: e.target.value})}
                  placeholder="e.g. 1, 2, 3"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Group</label>
                <input
                  type="text"
                  value={formData.group}
                  onChange={(e) => setFormData({...formData, group: e.target.value})}
                  placeholder="e.g. bridal-party, table-1"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>RSVP Status</label>
                <select
                  value={formData.rsvpStatus}
                  onChange={(e) => setFormData({...formData, rsvpStatus: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Dietary Requirements</label>
                <input
                  type="text"
                  value={formData.dietaryRequirements}
                  onChange={(e) => setFormData({...formData, dietaryRequirements: e.target.value})}
                  placeholder="e.g. Vegetarian, Gluten-free, No nuts"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any additional notes about this guest"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: '#F9FAFB',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#6B9080'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingGuest(null);
                    setFormData({ name: '', email: '', dietaryRequirements: '', rsvpStatus: 'pending' });
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: submitting ? '#9CA3AF' : '#6B9080',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Guests;
